// pages/admin/NotificationPreferences.js — per-event / per-channel toggles
// for the 19 ops-bus events. Absence of an override = enabled.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Bell, Mail, MessageSquare, Loader2 } from 'lucide-react';

const CHANNEL_META = [
  { key: 'in_app', label: 'In-app', Icon: Bell },
  { key: 'email', label: 'Email', Icon: Mail },
  { key: 'sms', label: 'SMS', Icon: MessageSquare },
];

export default function AdminNotificationPreferences() {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/notification-preferences');
        setEvents(res.data.events);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load preferences');
        setEvents([]);
      }
    })();
  }, []);

  const applyChanges = async (changes, next) => {
    const prev = events;
    setEvents(next);
    setSaving(true);
    try {
      await api.put('/admin/notification-preferences', { changes });
    } catch (e) {
      setEvents(prev);
      toast.error(e.response?.data?.detail || 'Failed to save — reverted');
    } finally {
      setSaving(false);
    }
  };

  const toggleOne = (eventType, channel, enabled) => {
    const next = events.map((ev) =>
      ev.event_type === eventType
        ? { ...ev, channels: { ...ev.channels, [channel]: enabled } }
        : ev
    );
    applyChanges([{ event_type: eventType, channel, enabled }], next);
  };

  const toggleColumn = (channel) => {
    // If any event is ON for this channel, turn all OFF; else all ON.
    const anyOn = events.some((ev) => ev.channels[channel]);
    const enabled = !anyOn;
    const changes = events.map((ev) => ({ event_type: ev.event_type, channel, enabled }));
    const next = events.map((ev) => ({ ...ev, channels: { ...ev.channels, [channel]: enabled } }));
    applyChanges(changes, next);
  };

  if (events === null) {
    return (
      <div className="min-h-screen bg-slate-50 grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="notification-preferences-page">
      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="notif-prefs-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-cyan-600" />
            Notification Preferences
          </h1>
          {saving && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </div>

        <p className="text-sm text-slate-600 px-1">
          Choose which ops events reach you on each channel. These settings apply only
          to your account — other admins keep their own.
        </p>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_repeat(3,56px)] sm:grid-cols-[1fr_repeat(3,90px)] items-center gap-1 px-4 py-3 bg-slate-50 border-b border-slate-200">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Event</div>
            {CHANNEL_META.map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => toggleColumn(key)}
                title={`Toggle all ${label}`}
                data-testid={`toggle-all-${key}`}
                className="flex flex-col sm:flex-row items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-cyan-700 transition-colors"
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {events.map((ev) => (
            <div
              key={ev.event_type}
              className="grid grid-cols-[1fr_repeat(3,56px)] sm:grid-cols-[1fr_repeat(3,90px)] items-center gap-1 px-4 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
              data-testid={`pref-row-${ev.event_type}`}
            >
              <div className="text-sm text-slate-800 pr-2">{ev.label}</div>
              {CHANNEL_META.map(({ key }) => (
                <div key={key} className="flex justify-center">
                  <Switch
                    checked={ev.channels[key]}
                    onCheckedChange={(v) => toggleOne(ev.event_type, key, v)}
                    data-testid={`pref-${ev.event_type}-${key}`}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>

        <p className="text-xs text-slate-500 px-1">
          Tap a column header to toggle every event for that channel at once.
          Recipients configured by phone/email only (without an admin account) are not affected.
        </p>
      </div>
    </div>
  );
}
